import { DeclarationWorkbookViewer } from "@/components/common/ExcelViewer/DeclarationWorkbookViewer"
import {
  DeclarationDocumentViewer,
  type Highlight,
  type SourceRegion,
} from "@/components/common/PdfViewer/DeclarationDocumentViewer"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { FileText } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useDocumentBlobUrl } from "../../studio/flowShared"
import type { FilterToken } from "../../studio/TableSearch"
import type { WorkbookSource } from "../../studio/workbookProofs"
import type { Zoom } from "../studioV2Url"
import {
  captureEvidenceSelection,
  clampSelectionToAnchorPage,
  type EvidencePick,
} from "./evidenceSelection"
import {
  captureMarkSelection,
  type MarkCapture,
  type PassageMark,
} from "./markSelection"
import { PageIndicator } from "./PageIndicator"
import { ZoomControl } from "./ZoomControl"

/** The gap the chip keeps from the selection, mirroring the 8px both
 *  `.sv2-selchip` transforms carry. */
const CHIP_GAP = 8
/** The chip's own height, an ESTIMATE (6px padding twice plus a ~20px line
 *  box), never a measurement: the chip does not exist yet when the placement
 *  is decided. Only used to ask whether it would still fit UNDER the
 *  selection, so erring high just flips a borderline case back above. */
const CHIP_HEIGHT_ESTIMATE = 32

/** Stable defaults: a fresh `[]` or `() => {}` per render would hand the
 *  workbook viewer new props each render, and each costs a full redraw. */
const NO_WORKBOOK_SOURCES: WorkbookSource[] = []
const NO_BAR_OPEN_CHANGE = () => {}

/**
 * Where the evidence chip hangs off the selection's rect.
 *
 * BELOW it, by default: anchored at the selection's `top` (and lifted a full
 * chip height by `.sv2-selchip`'s transform) the chip covered the line ABOVE
 * the selection, which in a dense annex table is the row the reviewer is
 * reading (Pablo, 17 Sep: "marking text in pdf-preview ... feels buggy").
 *
 * Flipped back above when the selection sits too close to the viewport's
 * bottom edge for the chip to fit underneath it, since the chip is
 * fixed-positioned and would otherwise hang off-screen.
 */
function chipPlacement(rect: DOMRect): {
  x: number
  y: number
  below: boolean
} {
  const below =
    rect.bottom + CHIP_GAP + CHIP_HEIGHT_ESTIMATE <= window.innerHeight
  return {
    x: rect.left + rect.width / 2,
    y: below ? rect.bottom : rect.top,
    below,
  }
}

/**
 * Does this DOCUMENT-level mouseup/keyup belong to the pane's own body?
 *
 * Either it happened inside it, or — for a drag that started on the page and
 * was RELEASED outside it, over the review mask — the selection's own anchor
 * node is still inside it. The second clause is the fix: the listener used to
 * be bound on the body itself, so such a drag fired nothing at all and no
 * chip appeared ("marking text ... feels buggy", Pablo, 17 Sep). A gesture
 * with neither its target nor its selection in the body is somebody else's
 * and is ignored, exactly as before.
 */
function isOwnSelectionEvent(body: HTMLElement, e: Event): boolean {
  const target = e.target
  if (target instanceof Node && body.contains(target)) return true
  const anchor = window.getSelection()?.anchorNode
  return Boolean(anchor && body.contains(anchor))
}

/** A unit card's evidence flash — the SAME `highlight`/`sources` pair v1's
 *  `StudioScreenA::showAnchor` sets on a citation click, scoped to ONE
 *  statement at a time. `highlight` is what actually scrolls + paints/flashes
 *  (a fresh object per click, so two clicks on the SAME quote still both
 *  animate — see `DeclarationDocumentViewer`'s own `highlight` doc); `sources`
 *  is the statement's FULL evidence set, carried alongside so a quote the text
 *  layer cannot find still renders its backend `bbox` as a fallback box
 *  (`DeclarationDocumentViewer`'s `boxesByPage`), exactly like v1. */
export interface EvidenceFlash {
  highlight: Highlight
  sources: SourceRegion[]
  activeStatementId: string
}

export interface DocumentPaneProps {
  declId: string
  /** Which viewer reads the bytes — derived by the caller from the
   *  declaration's OWN file name (`isWorkbookFileName`), not from the
   *  extraction read model: `document_kind` is absent until a run has
   *  completed at least once, so keying this off it opened the PDF viewer on
   *  a workbook's raw bytes for as long as extraction was pending,
   *  processing, re-evaluating or failed (see `StudioV2Layout`'s own
   *  `isWorkbook` for the full account). */
  isWorkbook: boolean
  zoom: Zoom
  onZoomChange: (zoom: Zoom) => void
  /** A page-addressed jump (a unit card's "S. {page}" button) — a fresh
   *  `token` on the SAME page still re-triggers the scroll+flash, so two
   *  clicks on the same evidence in a row both visibly re-locate it. Null
   *  when nothing has been requested this session. */
  jumpToPage: { page: number; token: string } | null
  /** A unit card's page-chip/header click — see `EvidenceFlash`'s own doc.
   *  Threaded to the viewer's EXISTING `highlight`/`sources`/
   *  `activeStatementId` props (dead-wired to `null`/`[]`/`null` before this),
   *  never a new mechanism. Null when nothing has been flashed this session,
   *  or once the caller clears it (a framework switch — stale evidence must
   *  never linger over the wrong Verordnung's cards). PDF-only — a workbook
   *  declaration's own click routes through `workbookSources`/
   *  `workbookFocus` below instead, never through this PDF-shaped type (see
   *  `EvidenceFlash`'s own doc for why the two are not folded together). */
  evidenceFlash?: EvidenceFlash | null
  /** The active framework's cell proofs, already resolved by
   *  `buildWorkbookSources` (see `StudioV2Layout`'s own memo) — the workbook
   *  twin of `evidenceFlash.sources`. Empty (or omitted) on a PDF
   *  declaration, or before any active card carries cell evidence. */
  workbookSources?: WorkbookSource[]
  /** A unit card's cell-evidence chip/header click on a WORKBOOK
   *  declaration — the key-addressed twin of `evidenceFlash` (a cell proof
   *  IS an address, so the gesture names the mark rather than a quote to
   *  hunt for, exactly like v1's own `workbookFocus`). Null when nothing has
   *  been focused this session, or once the caller clears it (a framework
   *  switch). Threaded straight to `DeclarationWorkbookViewer`'s own
   *  `focusRequest` prop. */
  workbookFocus?: { key: string } | null
  /** A reader hint's sheet row (1-based) to land on and tint, on a WORKBOOK
   *  declaration (`StudioV2Layout`'s own `workbookRowFocus`). Null when no
   *  hint was clicked, or once the caller drops it (a proof jump, a framework
   *  switch). Threaded straight to `DeclarationWorkbookViewer`'s own
   *  `rowFocus` prop. */
  workbookRowFocus?: { sheet: string; row: number } | null
  /** The viewer's bar stepped (◀▶ on a proof or a search hit) away from the
   *  hinted row: the caller drops `workbookRowFocus`. Threaded straight to
   *  `DeclarationWorkbookViewer`'s own `onRowFocusEnd` prop. */
  onWorkbookRowFocusEnd?: () => void
  /** Which statement's workbook proofs stay persistently tinted — threaded
   *  straight to `DeclarationWorkbookViewer`'s own `activeStatementId` prop.
   *  PDF's equivalent concept lives inside `evidenceFlash.activeStatementId`
   *  instead (a separate prop here because the two viewers' `sources` types
   *  are not the same shape). Null when nothing is focused. */
  activeStatementId?: string | null
  /** The declaration's own file name (`Declaration.doc`), for the pane
   *  header's `.sv2-t` — optional so a caller with no declaration on hand
   *  (a standalone test) renders the header without it. */
  fileName?: string
  /** TUR-1445's selection chip: whenever the reviewer selects text in the
   *  PDF and `captureEvidenceSelection` can vouch for it, a small floating
   *  chip appears at the selection carrying this label ("+ Als Aussage
   *  hinzufügen", or "Als Fundstelle übernehmen" while the add dialog is
   *  open — the layout decides). Clicking it fires `onEvidencePick`. No
   *  mode, no banner: the affordance exists exactly when a selection does.
   *  PDF-only; the workbook branch never reads these (canvas grid, no DOM
   *  text to select). Omitting either disables the feature. */
  evidencePickLabel?: string
  onEvidencePick?: (pick: EvidencePick) => void
  /** TUR-1211 (W5-1): puts the pane into passage-marking mode for the ONE
   *  statement whose ask box is asking for it: a selection then becomes a
   *  mark (`captureMarkSelection`) instead of an evidence pick, and the mark
   *  chip ("Passage markieren") replaces the evidence chip. Mutually
   *  exclusive with `onEvidencePick` at the caller's discretion; this
   *  component itself just prefers marking when both are on. */
  markMode?: boolean
  /** TUR-1211 (W5-2): flips the caller's `markMode` state. Lives here rather
   *  than a plain "is marking on" boolean because the toggle BUTTON itself
   *  is rendered by this component now (in the toolbar, `.sv2-pnh`, next to
   *  the zoom control): it used to float over the layout's own global top
   *  bar (`StudioV2Layout`, absolute-positioned), sitting behind the
   *  notification bell and the theme button there. `StudioV2Layout` still
   *  owns the state/setter; this is only the callback. Omitted (a workbook
   *  declaration, or a standalone test) renders no button at all, same
   *  guard the old floating button had, just enforced here instead: there
   *  is no PDF page on a workbook declaration to mark boxes on, and this
   *  component's own `isWorkbook` branch never reaches the toolbar row that
   *  would render it. */
  onToggleMarkMode?: () => void
  /** The active statement's own marks so far, rendered as numbered tinted
   *  boxes on their page. Empty/omitted renders nothing extra. */
  marks?: PassageMark[]
  /** TUR-1211 (W5-1): fires when the mark chip is clicked, the capture
   *  alongside the chip's OWN fixed-viewport coordinates (`markChip.x`/`.y`
   *  below): the anchor `MarkQuestionPopover` opens at, so the popover
   *  replaces the chip in place instead of floating somewhere unrelated to
   *  the selection. */
  onMark?: (mark: MarkCapture, anchor: { x: number; y: number }) => void
  /** The find bar's own open/closed flag (TUR-1211 wave 5, L8), owned by
   *  `StudioV2Layout` rather than this pane: a local `useState` here reset
   *  to closed on every remount of this component (a framework switch, a
   *  mask sheet toggling), silently reopening a bar the reviewer had just
   *  closed. Closed by default; only a real gesture flips it (the find
   *  icon's nonce below, a user-initiated evidence/workbook jump, or the
   *  bar's own ⓘ/✕ toggle), never an automatic flash.
   *  Optional (defaulting to closed/no-op below) only so a read-only
   *  preview mount (`RequestQuestionsDialog`'s marked-passages panel, which
   *  owns no find bar of its own and never renders one) can omit the pair
   *  entirely rather than wire two dead props into a file someone else is
   *  actively redesigning. `StudioV2Layout`, the one mount this actually
   *  matters for, always passes both: the single-owner property this wave
   *  fixed is unchanged there. See the "the find icon opens the bar" test
   *  in `StudioV2Layout.test.tsx` for the guard against a future caller
   *  silently dropping the pair and shipping a bar that can never open. */
  barOpen?: boolean
  onBarOpenChange?: (open: boolean) => void
  /** The find bar's expression (chips + live input), owned by the layout
   *  rather than this pane: the article browser (`ReviewMaskPane`'s sheet)
   *  filters its table by the SAME expression while it is open, so the one
   *  search field of the screen serves both panes. */
  findTokens: FilterToken[]
  findSearch: string
  onFindTokensChange: (tokens: FilterToken[]) => void
  onFindSearchChange: (search: string) => void
  /** The article browser stands in the review-mask pane: its placeholder
   *  says the search covers the table too. Deliberately FALSE for the
   *  substances sheet (`StudioV2Layout`'s own doc: it does not consume the
   *  find-bar expression), so use `sheetOpen` below for anything that must
   *  react to ANY mask sheet, kind included. L8: opening the browser no
   *  longer opens or reopens the bar, this prop drives the placeholder
   *  text only now. */
  articleBrowserOpen?: boolean
  /** Bumped when the article browser's search icon is pressed: the layout
   *  opens the bar itself in the SAME write as this bump (`requestFindFocus`),
   *  so by the time this reaches the bar it only has to put the cursor into
   *  the input, which it does either way, open flip or not (the bar only
   *  focuses itself on its own open flip otherwise). */
  findFocusNonce?: number
  /** TUR-1211 wave 5, task 3: true while ANY mask sheet stands (the
   *  article browser, the remainder view, OR the substance table), unlike
   *  `articleBrowserOpen` above which stays false for the substances kind
   *  on purpose. Drives the mark-mode toggle's `disabled` only: marking and
   *  a mask sheet are mutually exclusive over this same pane no matter
   *  which kind is open (`StudioV2Layout` already turns `markMode` off for
   *  every kind), so the toggle must stay disabled for every kind too, not
   *  just the ones that also open the find bar. */
  sheetOpen?: boolean
}

/**
 * The right pane — the EXISTING v1 document viewers, reused as-is, now with
 * the D2 chrome above them: a live page readout (`onCurrentPage`) and the
 * zoom control, both wired to the SAME `DeclarationDocumentViewer` additive
 * props B9 introduced. Workbook zoom stays out of scope (plan §F.6) — the
 * chrome header renders only over the PDF viewer, which is the only one
 * `scale`/`onCurrentPage`/`jumpToPage` exist on.
 *
 * Markup follows the prototype's `.pane.pane-doc` (`.pnh` + `.pbody`) — the
 * body is a plain `sv2-pbody` around the viewer rather than the prototype's
 * own per-page `.pdfdoc`/`.pdfpg`/`.pgcap` image list: those belong to the
 * prototype's hand-rolled page renderer, and `DeclarationDocumentViewer`
 * (kept unchanged, per scope) owns its own page rendering and scrolling.
 */
export function DocumentPane({
  declId,
  isWorkbook,
  zoom,
  onZoomChange,
  jumpToPage,
  barOpen = false,
  onBarOpenChange = NO_BAR_OPEN_CHANGE,
  evidenceFlash = null,
  workbookSources = NO_WORKBOOK_SOURCES,
  workbookFocus = null,
  workbookRowFocus = null,
  onWorkbookRowFocusEnd,
  activeStatementId = null,
  fileName,
  evidencePickLabel,
  onEvidencePick,
  markMode = false,
  onToggleMarkMode,
  marks = [],
  onMark,
  findTokens,
  findSearch,
  onFindTokensChange,
  onFindSearchChange,
  articleBrowserOpen = false,
  findFocusNonce = 0,
  sheetOpen = false,
}: DocumentPaneProps) {
  // The mark-passage flow writes a COLLECTED QUESTION, and the backend
  // guards that write with `MC_DECLARATION_INQUIRY` (403 without it, twelve
  // refusals in one live session while this toolbar offered the whole
  // gesture). Same flag, same source as `AskBox`, which renders its
  // placeholder on the identical read: one gate, not two readings that can
  // drift. The evidence chip is a different feature and stays.
  const inquiryFlag = useFeatureFlag("MC_DECLARATION_INQUIRY")

  // A permission that goes away, or arrives late after the toggle was
  // already pressed, would otherwise leave marking ACTIVE behind a toggle
  // that is now dead: boxes could still be drawn into a flow whose save is
  // refused, with no control left to turn it off. Turning it off is the
  // caller's business, so this asks rather than reaching into its state.
  useEffect(() => {
    if (!inquiryFlag && markMode && onToggleMarkMode) onToggleMarkMode()
  }, [inquiryFlag, markMode, onToggleMarkMode])
  const { translate } = useTranslation()
  const {
    url: docBlobUrl,
    isPending,
    isError,
  } = useDocumentBlobUrl(declId, isWorkbook ? "workbook" : "pdf")
  const [currentPage, setCurrentPage] = useState<number | null>(null)
  const [totalPages, setTotalPages] = useState<number | null>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  // The floating selection chip (TUR-1445): a valid selection raises it AT
  // the selection; it dies with the selection (click-away, scroll). The pick
  // is captured at mouseup time — the selection may be gone by click time.
  const [selChip, setSelChip] = useState<{
    pick: EvidencePick
    x: number
    y: number
    below: boolean
  } | null>(null)
  // The mark-mode twin of `selChip` above: same lifecycle, separate state
  // because the two carry different payload shapes (`EvidencePick` vs
  // `MarkCapture`) and are never both live (mark mode wins in
  // `handlePickMouseUp` below).
  const [markChip, setMarkChip] = useState<{
    pick: MarkCapture
    x: number
    y: number
  } | null>(null)
  // Review wave int (F1): `captureMarkSelection` returning "too-long" is the
  // one refusal that is NOT quiet. A selection over the backend's cap must
  // still tell the reviewer why no mark appeared, or the feature reads as
  // broken. Same fixed-position, capture-time-coordinates lifecycle as
  // `markChip`, mutually exclusive with it (never both set).
  const [markTooLong, setMarkTooLong] = useState<{
    x: number
    y: number
  } | null>(null)

  const pickingEnabled = Boolean(onEvidencePick && evidencePickLabel)

  // The selection text of the chip currently standing — set ONLY when a
  // capture produced one, never for a refusal. `captureEvidenceSelection`
  // builds the page's whole text index and runs `findAllMatches` UNCACHED on
  // every call (`evidenceSelection.ts`), and it used to run on every mouseup
  // and keyup: a plain click, the keyup of a Cmd+C, and — since the listeners
  // moved to `document` — every gesture in the pane. Cleared together with
  // the chip (`clearSelChip`), so a selection whose chip died on a scroll can
  // still raise it again rather than reading as "already handled".
  const lastCaptureTextRef = useRef<string | null>(null)

  const clearSelChip = useCallback(() => {
    lastCaptureTextRef.current = null
    setSelChip(null)
  }, [])

  const handleSelectionEvent = useCallback(
    (e: Event) => {
      const body = bodyRef.current
      if (!body) return
      if (!isOwnSelectionEvent(body, e)) return
      // A COMPLETED POINTER GESTURE only. A keyboard extension must never be
      // clamped: Shift+Arrow was handed back to the browser two fixes ago so
      // the reviewer can grow a selection, including across a page boundary
      // for an ordinary copy, and clamping on keyup would yank it back on
      // every keystroke.
      const pointerGesture = e.type === "mouseup"
      if (markMode) {
        // Same clamp as the evidence branch below, for the same reason:
        // `captureMarkSelection` shares the refusal shape exactly (the same
        // `pageElementOf` anchor/focus test, and its own 1000-char cap
        // checked before the page checks, markSelection.ts:81-86), so a drag
        // that left the pane dies there too.
        if (pointerGesture) clampSelectionToAnchorPage(body)
        const result = captureMarkSelection(body)
        if (typeof result === "string") {
          setMarkChip(null)
          if (result !== "too-long") {
            setMarkTooLong(null)
            return
          }
          const rect = window
            .getSelection()
            ?.getRangeAt(0)
            .getBoundingClientRect()
          setMarkTooLong(
            rect ? { x: rect.left + rect.width / 2, y: rect.top } : null,
          )
          return
        }
        setMarkTooLong(null)
        const rect = window
          .getSelection()
          ?.getRangeAt(0)
          .getBoundingClientRect()
        if (!rect) return
        setMarkChip({
          pick: result,
          x: rect.left + rect.width / 2,
          y: rect.top,
        })
        return
      }
      if (!pickingEnabled) return
      // Guard BEFORE the capture, which is the expensive half (see
      // `lastCaptureTextRef`). Nothing selected: a plain click, or a
      // click-away that collapsed the previous selection.
      const selection = window.getSelection()
      if (!selection || selection.isCollapsed) {
        clearSelChip()
        return
      }
      // Cut a gesture that ran out of the pane back to the page it started
      // on, BEFORE anything measures it. Once the pointer leaves, the browser
      // extends the selection across the app's own DOM, and the capture
      // checks the quote's LENGTH before it looks at pages at all — so an
      // unclamped drag off the pane is refused as "too-long" and the page
      // logic never runs. Cheap and a no-op for a selection already inside
      // one page; it rewrites the live selection, so the text has to be read
      // back afterwards.
      if (pointerGesture) clampSelectionToAnchorPage(body)
      const text = window.getSelection()?.toString().trim() ?? ""
      if (!text) {
        clearSelChip()
        return
      }
      // The same selection, seen again (a second mouseup over it, the keyup
      // of a Cmd+C): its chip already stands, there is nothing to recompute.
      if (text === lastCaptureTextRef.current) return
      const result = captureEvidenceSelection(body)
      if (typeof result === "string") {
        // Invalid selections (cross-page, unfindable, stray click) simply
        // raise no chip — the same quiet contract as any selection UI. NOT
        // memoized: a refusal would otherwise be permanent, because nothing
        // invalidates the memo while no chip stands (the selectionchange and
        // scroll listeners below only exist while `selChip` is set) and a
        // re-drag never produces the plain click that would clear it. The
        // reviewer who over-drags across a page boundary and then reselects
        // the same words on one page has to get a chip.
        //
        // `clearSelChip`, not a bare `setSelChip(null)`: the memo is about to
        // outlive the chip it belongs to. An OLDER capture's text was still in
        // `lastCaptureTextRef` here, so re-selecting exactly that passage hit
        // the "same selection, seen again" early return above and raised no
        // chip ever again — the very passage the refused gesture started from
        // became unpickable. The two retire together (Copilot on #2327).
        clearSelChip()
        return
      }
      const rect = window.getSelection()?.getRangeAt(0).getBoundingClientRect()
      if (!rect) return
      // Memoized only now, on the path that actually raises a chip. That is
      // what keeps the memo self-invalidating: it is non-null only while a
      // chip stands, and `clearSelChip` retires the two together.
      lastCaptureTextRef.current = text
      setSelChip({ pick: result, ...chipPlacement(rect) })
    },
    [markMode, pickingEnabled, clearSelChip],
  )

  // Bound on `document`, NOT on `.sv2-pbody` (fix P-g). A drag released
  // outside the pane — over the review mask, the usual way to pick up a table
  // row's last line — fires its mouseup on whatever is under the cursor, so a
  // body-bound listener never saw it and the chip simply did not appear.
  // `isOwnSelectionEvent` filters the document's traffic back down to this
  // pane. No `capture: true` here (unlike the scroll listeners above, which
  // need it because scroll does not bubble): mouseup and keyup both do.
  // keyup as well as mouseup: a selection grown with Shift+Arrow must raise
  // the chip exactly like a mouse drag (Copilot, PR #2057).
  useEffect(() => {
    document.addEventListener("mouseup", handleSelectionEvent)
    document.addEventListener("keyup", handleSelectionEvent)
    return () => {
      document.removeEventListener("mouseup", handleSelectionEvent)
      document.removeEventListener("keyup", handleSelectionEvent)
    }
  }, [handleSelectionEvent])

  // The chip follows the selection's lifetime: collapsing it (a click
  // elsewhere) takes the chip with it. Scrolling the document also clears —
  // the chip is fixed-positioned at capture-time coordinates and would
  // otherwise drift off its passage.
  useEffect(() => {
    if (!selChip) return undefined
    const onSelectionChange = () => {
      const selection = window.getSelection()
      if (!selection || selection.isCollapsed) clearSelChip()
    }
    // Scroll doesn't collapse a selection, so it needs its own listener; the
    // actual scroll container is nested inside the PDF viewer, hence capture.
    // `clearSelChip`, not a bare `setSelChip(null)`: the selection outlives
    // the scroll, so the capture memo has to go with the chip or the same
    // selection could never raise it again.
    const onScroll = () => clearSelChip()
    document.addEventListener("selectionchange", onSelectionChange)
    document.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    })
    return () => {
      document.removeEventListener("selectionchange", onSelectionChange)
      document.removeEventListener("scroll", onScroll, { capture: true })
    }
  }, [selChip, clearSelChip])

  // The mark chip's own lifecycle twin of `selChip`'s effect above: it
  // collapses with the selection (a click elsewhere) and clears on scroll,
  // for the exact same reason (a fixed-positioned chip at capture-time
  // coordinates would otherwise drift off its passage).
  useEffect(() => {
    if (!markChip) return undefined
    const onSelectionChange = () => {
      const selection = window.getSelection()
      if (!selection || selection.isCollapsed) setMarkChip(null)
    }
    const onScroll = () => setMarkChip(null)
    document.addEventListener("selectionchange", onSelectionChange)
    document.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    })
    return () => {
      document.removeEventListener("selectionchange", onSelectionChange)
      document.removeEventListener("scroll", onScroll, { capture: true })
    }
  }, [markChip])

  // The too-long hint's own lifecycle twin of `markChip`'s effect above, for
  // the same reason: it dies with the selection or a scroll rather than
  // drifting off the passage it named (review wave int, F1).
  useEffect(() => {
    if (!markTooLong) return undefined
    const onSelectionChange = () => {
      const selection = window.getSelection()
      if (!selection || selection.isCollapsed) setMarkTooLong(null)
    }
    const onScroll = () => setMarkTooLong(null)
    document.addEventListener("selectionchange", onSelectionChange)
    document.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    })
    return () => {
      document.removeEventListener("selectionchange", onSelectionChange)
      document.removeEventListener("scroll", onScroll, { capture: true })
    }
  }, [markTooLong])

  const confirmPick = () => {
    if (!selChip) return
    window.getSelection()?.removeAllRanges()
    // `clearSelChip`, so re-selecting the SAME passage right after adding it
    // still raises a chip instead of reading as an unchanged selection.
    clearSelChip()
    onEvidencePick?.(selChip.pick)
  }

  const confirmMark = () => {
    if (!markChip) return
    window.getSelection()?.removeAllRanges()
    const anchor = { x: markChip.x, y: markChip.y }
    setMarkChip(null)
    onMark?.(markChip.pick, anchor)
  }

  // TUR-1211 wave 5, L8: the bar no longer opens ITSELF from an effect in
  // this component. `evidenceFlash`/`workbookFocus` opening the bar (v1's
  // "the bar opens with the jump", `StudioScreenA`'s own doc), the find
  // icon's nonce, and the article browser standing open are now three
  // things `StudioV2Layout` decides directly, at the one call site that
  // actually knows whether the jump was a click or an auto-flash, see
  // `handleJumpToEvidence`'s own `auto` parameter and `requestFindFocus`
  // there. This pane only ever reads `barOpen` now; it never writes it on
  // its own initiative, so a fresh mount (a framework switch, a mask sheet
  // toggling) cannot reopen what the reviewer closed, and opening the
  // article browser no longer pops the bar open at all (target behaviour,
  // this wave: the browser keeps its own "Filter" chip for that).

  const placeholder = translate(
    articleBrowserOpen
      ? "MC_DECL_V2_DOC_FIND_PLACEHOLDER_BROWSER"
      : "MC_DECL_V2_DOC_FIND_PLACEHOLDER",
  )

  if (isPending) {
    return (
      <div className="sv2-pane sv2-pane-doc">
        <div className="flex h-full items-center justify-center">
          <span
            className="h-4 w-4 animate-spin rounded-full border-2 border-grey5 border-t-transparent"
            aria-hidden="true"
          />
        </div>
      </div>
    )
  }

  if (!docBlobUrl || isError) {
    return (
      <div className="sv2-pane sv2-pane-doc">
        <div className="flex h-full items-center justify-center px-6 text-center text-[12px] text-grey5">
          {translate("MC_DECL_V2_DOC_UNAVAILABLE")}
        </div>
      </div>
    )
  }

  if (isWorkbook) {
    return (
      <div className="sv2-pane sv2-pane-doc">
        {/* The PDF branch's own header chrome (icon + filename), minus the
         *  two controls that only exist for it: `PageIndicator` (no PDF
         *  page concept — the workbook's own "surface ordinal" is not a
         *  page number, see `DocumentPaneProps.jumpToPage`'s own doc) and
         *  `ZoomControl` (workbook zoom is a documented non-goal, plan
         *  §F.6). Without this the reviewer had no way to see which file
         *  they were looking at on a workbook declaration at all. */}
        <div className="sv2-pnh">
          <FileText className="sv2-ic" />
          {fileName && <span className="sv2-t">{fileName}</span>}
        </div>
        <div className="sv2-pbody sv2-wbbody">
          <DeclarationWorkbookViewer
            fileUrl={docBlobUrl}
            sources={workbookSources}
            activeStatementId={activeStatementId}
            focusRequest={workbookFocus}
            rowFocus={workbookRowFocus}
            onRowFocusEnd={onWorkbookRowFocusEnd}
            open={barOpen}
            onOpenChange={onBarOpenChange}
            findFocusNonce={findFocusNonce}
            tokens={findTokens}
            search={findSearch}
            onTokensChange={onFindTokensChange}
            onSearchChange={onFindSearchChange}
            placeholder={placeholder}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="sv2-pane sv2-pane-doc">
      <div className="sv2-pnh">
        <FileText className="sv2-ic" />
        {fileName && <span className="sv2-t">{fileName}</span>}
        <PageIndicator current={currentPage} total={totalPages} />
        {/* TUR-1211 (W5-2): the mark-mode toggle lives in this toolbar row now
            (see `onToggleMarkMode`'s own doc): right-aligned, immediately
            before the zoom control, styled like the toolbar's other action
            buttons (`.sv2-act`, the same class the footer's own action
            buttons use) rather than the zoom stepper's bare `.sv2-zb` glyph
            buttons, since this one carries a text label. `.sv2-on` is the
            toolbar's existing "pressed" convention (`.sv2-cbx.sv2-on`,
            `.sv2-act.sv2-aussagebtn.sv2-on`), paired with `aria-pressed` for
            the same state read accessibly. No toggle at all when the caller
            omits `onToggleMarkMode` (a standalone test, or a caller with no
            marking feature, e.g. `RequestQuestionsDialog`).
            Disabled while `sheetOpen` (fix 3h, widened task 3): marking and
            a mask sheet are mutually exclusive over this same pane no
            matter which KIND is open (`StudioV2Layout` already turns
            marking off for every kind): `sheetOpen` covers all of them,
            unlike `articleBrowserOpen`, which stays false for the
            substances kind on purpose (its own doc). Stays disabled for as
            long as the sheet stays open, not only reacting to the moment it
            opened. */}
        {onToggleMarkMode &&
          (() => {
            const toggle = (
              <button
                type="button"
                data-testid="mark-mode-toggle"
                aria-pressed={markMode}
                disabled={sheetOpen || !inquiryFlag}
                className={
                  markMode
                    ? "sv2-act sv2-marktoggle sv2-on"
                    : "sv2-act sv2-marktoggle"
                }
                onClick={onToggleMarkMode}
              >
                {translate(
                  markMode
                    ? "MC_DECL_V2_MARK_MODE_OFF"
                    : "MC_DECL_V2_MARK_MODE_ON",
                )}
              </button>
            )
            if (inquiryFlag) return toggle
            // THE REASON HANGS OFF A WRAPPER, NEVER OFF THE DEAD BUTTON
            // (Copilot on #2327). `.sv2-act[disabled]` sets `pointer-events:
            // none` (studioV2.css), so the `title` this used to carry could
            // not be hovered and a disabled button cannot be focused either:
            // the one sentence explaining the dead control was unreachable by
            // any means. The wrapper is not disabled, so it takes the hover,
            // and `tabIndex` puts it in the tab order so a keyboard reviewer
            // gets the same sentence. Same shape as `AskBoxPlaceholder`'s own
            // disabled send button.
            //
            // One cause, one sentence: the same copy the refused save raises
            // as a toast (`useCreateQuestion`'s 403 branch), so a reviewer who
            // meets both is not given two explanations for one permission.
            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span data-testid="mark-mode-hint" tabIndex={0}>
                      {toggle}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {translate("MC_DECL_V2_QUESTIONS_NOT_ENABLED")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          })()}
        <ZoomControl zoom={zoom} onChange={onZoomChange} />
      </div>
      {/* TUR-1445's floating selection chip — rendered as a SIBLING of the
          body (fixed positioning cares nothing for DOM place), so its own
          mouseup never re-enters the capture handler. `onMouseDown`
          preventDefault keeps the text selection alive through the click. */}
      {selChip && pickingEnabled && !markMode && (
        <button
          type="button"
          className={
            selChip.below ? "sv2-selchip sv2-selchip-below" : "sv2-selchip"
          }
          style={{ left: selChip.x, top: selChip.y }}
          data-testid="selection-chip"
          onMouseDown={(e) => e.preventDefault()}
          onClick={confirmPick}
        >
          {evidencePickLabel}
        </button>
      )}
      {markChip && markMode && (
        <button
          type="button"
          className="sv2-selchip"
          style={{ left: markChip.x, top: markChip.y }}
          data-testid="selection-chip"
          onMouseDown={(e) => e.preventDefault()}
          onClick={confirmMark}
        >
          {translate("MC_DECL_V2_MARK_CHIP")}
        </button>
      )}
      {/* Review wave int (F1): the one refusal the reviewer must be told
          about, not swallowed quietly like the others. Not a button: there
          is no valid capture behind it to confirm. */}
      {markTooLong && markMode && (
        <p
          className="sv2-marktoolong"
          style={{ left: markTooLong.x, top: markTooLong.y }}
          data-testid="mark-too-long-hint"
        >
          {translate("MC_DECL_V2_MARK_TOO_LONG")}
        </p>
      )}
      {/* The selection listeners live on `document` now (see the effect
          above), so this is a plain container again. */}
      <div className="sv2-pbody" ref={bodyRef}>
        <DeclarationDocumentViewer
          fileUrl={docBlobUrl}
          highlight={evidenceFlash?.highlight ?? null}
          sources={evidenceFlash?.sources ?? []}
          activeStatementId={evidenceFlash?.activeStatementId ?? null}
          open={barOpen}
          onOpenChange={onBarOpenChange}
          findFocusNonce={findFocusNonce}
          tokens={findTokens}
          search={findSearch}
          onTokensChange={onFindTokensChange}
          onSearchChange={onFindSearchChange}
          placeholder={placeholder}
          scale={zoom === 1 ? undefined : zoom}
          onCurrentPage={(page, total) => {
            setCurrentPage(page)
            setTotalPages(total)
          }}
          jumpToPage={jumpToPage}
          overlayQuotes
          marks={marks}
        />
      </div>
    </div>
  )
}
